import { Edit } from '@mui/icons-material'
import {
  Button,
  Chip,
  Grid,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import './QuoteList.css'
import { useContext, useEffect, useState } from 'react'
import { dataContext } from '..'
import axios from 'axios'
import { quoteType } from '../types'
import dayjs from 'dayjs'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import ErrorScreen from '../Misc/ErrorScreen'

const COLUMN_WIDTHS = {
  quoteNumber: '10%',
  crmAccount: '10%',
  crmContact: '10%',
  crmOpportunity: undefined,
  quoteTotal: '10%',
  grossProfit: '10%',
  createdDate: '10%',
  lastModifiedDate: '10%',
  status: '5%',
  edit: '40px',
}

export default function QuoteList() {
  const { user } = useContext(dataContext)
  const { state } = useLocation()
  const navigate = useNavigate()

  const [quotes, setQuotes] = useState<(quoteType & { CRMAccount: string; CRMContact: string })[]>([])
  const [quoteCount, setQuoteCount] = useState(0)
  const [filters, setFilters] = useState({
    quoteNumber: '',
    crmAccount: '',
    crmContact: '',
    crmOpportunity: '',
    quoteStatus: '',
    page: 0,
    pageSize: 25,
    sort: 'CreatedDate',
    sortDirection: 'desc' as 'asc' | 'desc',
  })
  const [listLoading, setListLoading] = useState(true)
  const [openDeleteSnackbar, setOpenDeleteSnackbar] = useState(state?.deleted ?? false)
  const [listLoadError, setListLoadError] = useState(false)

  useEffect(() => {
    if (!user.email) {
      return
    }

    handleSearch(filters)
  }, [user.email, filters.sort, filters.sortDirection, filters.page, filters.pageSize])

  const handleSearch = (_filters: typeof filters) => {
    setListLoading(true)
    setListLoadError(false)
    axios
      .get(
        `/quotes?quoteNumber=${_filters.quoteNumber}&crmAccount=${_filters.crmAccount}&crmContact=${_filters.crmContact}` +
          `&crmOpportunity=${_filters.crmOpportunity}&quoteStatus=${_filters.quoteStatus}&page=${_filters.page}&pageSize=` +
          `${_filters.pageSize}&sort=${_filters.sort}&sortDirection=${_filters.sortDirection}` +
          (!user.role.admin && !user.role.operations ? `&owner=${user.email}` : ''),
      )
      .then(res => {
        setQuotes(res.data.list)
        setQuoteCount(res.data.count)
      })
      .catch(err => {
        console.error(err)
        setListLoadError(true)
      })
      .finally(() => {
        setListLoading(false)
      })
  }

  return (
    <div className="quote-list-container">
      <form className="quote-list-filters">
        <Grid container spacing="24px">
          <Grid size={2.4}>
            <Typography variant="body2">Quote #:</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              size="small"
              label="Quote #"
              value={filters.quoteNumber}
              onChange={e => setFilters({ ...filters, quoteNumber: e.target.value })}
            />
          </Grid>
          <Grid size={2.4}>
            <Typography variant="body2">CRM Account:</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              size="small"
              label="CRM Account"
              value={filters.crmAccount}
              onChange={e => setFilters({ ...filters, crmAccount: e.target.value })}
            />
          </Grid>
          <Grid size={2.4}>
            <Typography variant="body2">CRM Contact:</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              size="small"
              label="CRM Contact"
              value={filters.crmContact}
              onChange={e => setFilters({ ...filters, crmContact: e.target.value })}
            />
          </Grid>
          <Grid size={2.4}>
            <Typography variant="body2">CRM Opportunity:</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              size="small"
              label="CRM Opportunity"
              value={filters.crmOpportunity}
              onChange={e => setFilters({ ...filters, crmOpportunity: e.target.value })}
            />
          </Grid>
          <Grid size={2.4}>
            <Typography variant="body2">Quote Status:</Typography>
            <TextField
              fullWidth
              autoComplete="off"
              size="small"
              label="Quote Status"
              value={filters.quoteStatus}
              onChange={e => setFilters({ ...filters, quoteStatus: e.target.value })}
            />
          </Grid>
        </Grid>
        <div>
          <Button
            variant="outlined"
            onClick={() => {
              setFilters({
                quoteNumber: '',
                crmAccount: '',
                crmContact: '',
                crmOpportunity: '',
                quoteStatus: '',
                page: 0,
                pageSize: 25,
                sort: 'CreatedDate',
                sortDirection: 'desc',
              })
              handleSearch({
                quoteNumber: '',
                crmAccount: '',
                crmContact: '',
                crmOpportunity: '',
                quoteStatus: '',
                page: 0,
                pageSize: 25,
                sort: 'CreatedDate',
                sortDirection: 'desc',
              })
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            type="submit"
            onClick={e => {
              e.preventDefault()
              handleSearch(filters)
            }}
          >
            Search
          </Button>
        </div>
      </form>
      {listLoadError ? (
        <ErrorScreen
          title="Error Loading Quotes"
          message="An error occurred while loading the quotes. Please try again or contact support if the problem persists."
        />
      ) : (
        <>
          <div className="quote-list-table-container">
            <div className="quote-list-table-header">
              <Typography variant="h6">Quotes</Typography>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                rowsPerPage={filters.pageSize}
                page={filters.page}
                onPageChange={(_, page) => setFilters({ ...filters, page })}
                onRowsPerPageChange={e => setFilters({ ...filters, pageSize: parseInt(e.target.value) })}
                count={quoteCount}
              />
            </div>
            <Table id="quote-list-table">
              <TableHead>
                <TableRow>
                  <TableCell width={COLUMN_WIDTHS.quoteNumber}>
                    <TableSortLabel
                      active={filters.sort === 'QuoteNumber'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'QuoteNumber',
                          sortDirection:
                            filters.sort === 'QuoteNumber' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      Quote #
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width={COLUMN_WIDTHS.crmAccount}>
                    <TableSortLabel
                      active={filters.sort === 'CRMAccount'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'CRMAccount',
                          sortDirection:
                            filters.sort === 'CRMAccount' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      CRM Account
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width={COLUMN_WIDTHS.crmContact}>
                    <TableSortLabel
                      active={filters.sort === 'CRMContact'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'CRMContact',
                          sortDirection:
                            filters.sort === 'CRMContact' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      CRM Contact
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width={COLUMN_WIDTHS.crmOpportunity}>
                    <TableSortLabel
                      active={filters.sort === 'CRMOpportunity'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'CRMOpportunity',
                          sortDirection:
                            filters.sort === 'CRMOpportunity'
                              ? filters.sortDirection === 'asc'
                                ? 'desc'
                                : 'asc'
                              : 'asc',
                        })
                      }
                    >
                      CRM Opportunity
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width={COLUMN_WIDTHS.quoteTotal}>
                    <TableSortLabel
                      active={filters.sort === 'QuoteTotal'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'QuoteTotal',
                          sortDirection:
                            filters.sort === 'QuoteTotal' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      Quote Total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width={COLUMN_WIDTHS.grossProfit}>
                    <TableSortLabel
                      active={filters.sort === 'GrossProfit'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'GrossProfit',
                          sortDirection:
                            filters.sort === 'GrossProfit' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      Gross Profit
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width={COLUMN_WIDTHS.createdDate}>
                    <TableSortLabel
                      active={filters.sort === 'CreatedDate'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'CreatedDate',
                          sortDirection:
                            filters.sort === 'CreatedDate' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      Created Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width={COLUMN_WIDTHS.lastModifiedDate}>
                    <TableSortLabel
                      active={filters.sort === 'LastModifiedDate'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'LastModifiedDate',
                          sortDirection:
                            filters.sort === 'LastModifiedDate'
                              ? filters.sortDirection === 'asc'
                                ? 'desc'
                                : 'asc'
                              : 'asc',
                        })
                      }
                    >
                      Last Modified Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width={COLUMN_WIDTHS.status}>
                    <TableSortLabel
                      active={filters.sort === 'Status'}
                      direction={filters.sortDirection}
                      onClick={() =>
                        setFilters({
                          ...filters,
                          sort: 'Status',
                          sortDirection:
                            filters.sort === 'Status' ? (filters.sortDirection === 'asc' ? 'desc' : 'asc') : 'asc',
                        })
                      }
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell width={COLUMN_WIDTHS.edit}>Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading
                  ? Array.from({ length: 10 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 10 }).map((_, index) => (
                          <TableCell key={index}>
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : quotes.map((quote, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <MuiLink component={RouterLink} to={`/quote/${quote.QuoteId}`}>
                            {quote.QuoteNumber}
                          </MuiLink>
                        </TableCell>
                        <TableCell>{quote.CRMAccount}</TableCell>
                        <TableCell>{quote.CRMContact}</TableCell>
                        <TableCell>{quote.CRMOpportunity}</TableCell>
                        <TableCell align="right">${quote.GrandTotal}</TableCell>
                        <TableCell align="right">${quote.GrossProfit}</TableCell>
                        <TableCell align="right">{dayjs(quote.CreatedDate).format('MM/DD/YYYY hh:mm A')}</TableCell>
                        <TableCell align="right">
                          {dayjs(quote.LastModifiedDate).format('MM/DD/YYYY hh:mm A')}
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={quote.Status} />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => navigate(`/quote/${quote.QuoteId}/lines`)}>
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              rowsPerPage={filters.pageSize}
              page={filters.page}
              onPageChange={(_, page) => setFilters({ ...filters, page })}
              onRowsPerPageChange={e => setFilters({ ...filters, pageSize: parseInt(e.target.value) })}
              count={quoteCount}
            />
          </div>
          <Snackbar
            autoHideDuration={5000}
            open={openDeleteSnackbar}
            onClose={() => setOpenDeleteSnackbar(false)}
            message="Quote deleted successfully"
          />
        </>
      )}
    </div>
  )
}
